import { ECarriersList, TCarrierFlow } from './Carrier';
import { claimStatusIds, IClaimCarrierIntegrationInfo } from './Claims';

export interface IClaimState {
	errorCode: number;
	errorMessage: string;
	triggerRefresh: boolean;
	calculated: boolean;
	redirect: boolean;
	claimData: IClaim;
	statusCalculation: TStatusCalculation;
	servicesMap: IService[];
	prescriptionSelectedFromStorage: boolean;
	doctorTypes: { name: string; label: string }[];
	reasonCodesVoid: IReasonCode[];
	reasonCodesCanceled: IReasonCode[];
	reasonCodesPending: IReasonCode[];
	reasonCodesReject: IReasonCode[];
	reasonCodesReturn: IReasonCode[];
	otherServices: { serviceLabel: string; serviceId: number | null }[];
	validationErrors: IValidationError[];
}

interface IReasonCode {
	code: string;
	groupCode: string;
	groupLabel: string;
	label: string;
	typologyCode: string;
	typologyLabel: string;
}

interface IServiceGroup {
	code: string;
	label: string;
}

interface IReceipt {
	number?: string;
	name?: string;
	vatCode?: string;
	receiptDate?: string;
	totalInvoiceAmount?: string;
}

export interface IService {
	code: string;
	label: string;
	group: IServiceGroup;
}

export type TStatusCalculation =
	| 'beforeCalculation'
	| 'afterCalculation'
	| 'onEdit';

export interface IClaimStatusHistory {
	id: claimStatusIds;
	name: string;
	label: string;
	reasonCode?: string;
	reasonCodeLabel?: string;
	internalNote?: string;
	createdBy?: {
		memberFirstName: string;
		memberLastName: string;
		memberRole: string;
		memberRoleLabel: string;
	};
	lastInsert?: string;
}

export interface IClaimAttachmentHistory {
	id?: number;
	type: 'PRESCRIPTION' | 'RECEIPT' | 'OTHER';
	uuid: string;
	fileName: string;
	lastInsert: string;
	createdBy?: {
		memberFirstName: string;
		memberLastName: string;
		memberRole: string;
		memberRoleLabel: string;
	};
}

export interface IClaimBillingHistory {
	id: number;
	name: string;
	label: string;
	createdBy?: {
		memberFirstName?: string | null;
		memberLastName?: string | null;
		memberRole: string;
		memberRoleLabel: string;
	};
	lastInsert: string;
}

export interface IClaimReturnStatus {
	code: string;
	label: string;
}

export interface IClaim {
	claimUUID?: string;
	externalClaimId: string;
	memberId: string;
	providerId: string;
	beneficiaryMemberId: string;
	beneficiarySubscriptionUuid: string;
	memberSubscriptionUuid: string;
	claimDate: string;
	step: number;
	// page 1
	exam: number;
	materials: number;
	lensType?: number;
	//page 2
	newPrescriptionContent?: IPrescription;
	oldPrescriptionContent?: IPrescription;
	prescriptionConsent: boolean;
	rxChangedWithoutOldPrescription: boolean | null;
	//page 3
	amount: {
		//basically everything is passed by calculate. only serviceid and retail amount is not
		components: IClaimComponents[];
		total: IClaimComponents;
		accumulator?: IClaimAccumulator;
	};
	rxChangeAttestation?: 0 | 1;
	uploadConsent: boolean;
	attachments: {
		prescription?: IFileType;
		receipt?: IFileType;
		others: IFileType[];
		return?: IFileType;
	};
	//useless
	memberFullName?: string;
	beneficiaryFullName?: string;
	memberFirstName?: string;
	memberLastName?: string;
	groupName?: string;
	memberProviderLocation?: string;
	status: {
		id: claimStatusIds;
		name: string;
		label: string;
		groupCode: string;
		groupLabel: string;
		internalNote?: string;
		reasonCode: string;
		reasonCodeLabel: string;
	};
	returnDetail?: {
		status: IClaimReturnStatus['code'];
		statusLabel: string;
		reasonCodeLabel: string;
		attachmentUuid: string;
		attachmentName: string;
	};
	planPayAmount?: number;
	memberPayAmount?: number;
	paidWith?: string;
	memberDesignation?: 'Subscriber' | 'Dependant';
	memberDesignationLabel?: string;
	planName?: string;
	oonProviderInfo?: {
		providerName?: string;
		providerLocation?: string;
		providerPhone?: string;
	};
	reimbursementInfo?: {
		ibanAccount?: string;
		bic?: string;
		bankName?: string;
		bankAddress?: string;
		beneficiaryName?: string;
	};
	signatureId?: number;
	servicesList?: string[];
	carrierIntegrationFlow?: TCarrierFlow; //TODO: not used, to be removed
	receipt: IReceipt | null;
	claimCarrierIntegrationInfo: IClaimCarrierIntegrationInfo;
	customLayout: ECarriersList;
	internalNote?: string;
	statusHistory?: IClaimStatusHistory[];
	attachmentHistory?: IClaimAttachmentHistory[];
	billingHistory?: IClaimBillingHistory[];
}

export type IClaimComponents = {
	retailAmount: number | string;
	retailNetOfTax: number | string;
	tax: number | string;
	total?: number;
	contractualAdjustmentAmount?: number;
	memberPayAmount?: number;
	planPayAmount?: number;
	serviceName?: string;
	serviceId: null | number;
	discountedAmount?: number;
	assessment?: number;
	allowablePlanPay?: number;
	coPay?: number;
	coInsurance?: number;
	disallowedAmount?: number;
	decliningBalance?: number;
	claimCostReduction?: number;
	inNetworkSave?: number;
	grossRetail?: number | string;
	inStorePromotion?: number | string;
};

export type IPrescription = {
	ava: number;
	prysm: number;
	decentralization: number;
	od: IEyePrescription;
	os: IEyePrescription;
	doctorTypology: string | null;
	prescriptionDate: string | null;
};

export type IEyePrescription = {
	sphere?: string;
	cylinder?: string;
	axis?: string;
	addition?: string;
	prismDioptresIO?: string;
	prismDirectionIO?: string;
	prismDioptresUD?: string;
	prismDirectionUD?: string;
	pd?: string;
	height?: string;
	vertexFitted?: string;
	faceformTilt?: string;
	pantoscopicTilt?: string;
};

type IClaimAccumulator = {
	benefitId: number;
	benefitType: string;
	accumulatorId: number;
	balance: number;
	maxUsableAmount: number;
	benefitUsed: number;
	planId: number;
	startDate: Date;
	endDate: Date;
	accumulatorType: string;
};

export type IFileType = {
	uuid: string;
	fileName?: string;
	lastInsertTs?: string;
	mime?: string;
	type?: string;
	size?: string;
};

export enum Eye {
	od = 'od',
	os = 'os',
}

export const PRESCRIPTION_DEFAULT = {
	ava: 0,
	prysm: 0,
	decentralization: 0,
	doctorTypology: null,
	prescriptionDate: null,
	od: {
		sphere: '',
		cylinder: '',
		axis: '',
		addition: '',
		prismDioptresIO: '',
		prismDirectionIO: '',
		prismDioptresUD: '',
		prismDirectionUD: '',
		pd: '',
		height: '',
		vertexFitted: '',
		faceformTilt: '',
		pantoscopicTilt: '',
	},
	os: {
		sphere: '',
		cylinder: '',
		axis: '',
		addition: '',
		prismDioptresIO: '',
		prismDirectionIO: '',
		prismDioptresUD: '',
		prismDirectionUD: '',
		pd: '',
		height: '',
		vertexFitted: '',
		faceformTilt: '',
		pantoscopicTilt: '',
	},
};

export const SERVICES_TYPE = {
	LENSES_ONLY: 1,
	FRAME_ONLY: 2,
	EXAM: 3,
	FRAME_AND_LENSES: 4,
	CONTACTS: 5,
	MONOFOCAL_LENS: 12,
	BIFOCAL_LENS: 13,
	TRIFOCAL_LENS: 14,
	PROGRESSIVE_LENS: 15,
	FRAME_AND_MONOFOCAL_LENS: 16,
	FRAME_AND_BIFOCAL_LENS: 17,
	FRAME_AND_TRIFOCAL_LENS: 18,
	FRAME_AND_PROGRESSIVE_LENS: 19,
	EXAM_VDU: 20,
};

export const LENS_TYPES = {
	MONOFOCAL_LENS: 1,
	BIFOCAL_LENS: 2,
	TRIFOCAL_LENS: 3,
	PROGRESSIVE_LENS: 4,
};

export const optionsExam: {
	label: string;
	labelCap?: string;
	value: number;
	enableIf: number[];
}[] = [
	{
		label: 'newClaim.exam.RoutineExam',
		value: SERVICES_TYPE.EXAM,
		enableIf: [SERVICES_TYPE.EXAM],
	},
	{
		label: 'newClaim.exam.RoutineExamVdu',
		value: SERVICES_TYPE.EXAM_VDU,
		enableIf: [SERVICES_TYPE.EXAM_VDU],
	},
	{
		label: 'newClaim.exam.NoExam',
		value: -1,
		enableIf: [
			SERVICES_TYPE.LENSES_ONLY,
			SERVICES_TYPE.FRAME_AND_LENSES,
			SERVICES_TYPE.CONTACTS,
			SERVICES_TYPE.FRAME_ONLY,
			SERVICES_TYPE.MONOFOCAL_LENS,
			SERVICES_TYPE.BIFOCAL_LENS,
			SERVICES_TYPE.TRIFOCAL_LENS,
			SERVICES_TYPE.PROGRESSIVE_LENS,
			SERVICES_TYPE.FRAME_AND_MONOFOCAL_LENS,
			SERVICES_TYPE.FRAME_AND_BIFOCAL_LENS,
			SERVICES_TYPE.FRAME_AND_TRIFOCAL_LENS,
			SERVICES_TYPE.FRAME_AND_PROGRESSIVE_LENS,
		],
	},
];

export const LENS_BENEFITS = [12, 13, 14, 15];
export const LENS_AND_FRAME_BENEFITS = [16, 17, 18, 19];
export const ALL_LENS_BENEFITS = [...LENS_BENEFITS, ...LENS_AND_FRAME_BENEFITS];

export const optionsMaterials: {
	label: string;
	labelCap?: string;
	value: number;
	enableIf: number[];
}[] = [
	{
		label: 'newClaim.materials.fl',
		labelCap: 'newClaim.materials.flCapitalized',
		value: SERVICES_TYPE.FRAME_AND_LENSES,
		enableIf: [
			SERVICES_TYPE.FRAME_AND_LENSES,
			SERVICES_TYPE.MONOFOCAL_LENS,
			SERVICES_TYPE.BIFOCAL_LENS,
			SERVICES_TYPE.TRIFOCAL_LENS,
			SERVICES_TYPE.PROGRESSIVE_LENS,
			SERVICES_TYPE.FRAME_AND_MONOFOCAL_LENS,
			SERVICES_TYPE.FRAME_AND_BIFOCAL_LENS,
			SERVICES_TYPE.FRAME_AND_TRIFOCAL_LENS,
			SERVICES_TYPE.FRAME_AND_PROGRESSIVE_LENS,
		],
	},
	{
		label: 'newClaim.materials.fo',
		value: SERVICES_TYPE.FRAME_ONLY,
		enableIf: [SERVICES_TYPE.FRAME_ONLY],
	},

	{
		label: 'newClaim.materials.lo',
		value: SERVICES_TYPE.LENSES_ONLY,
		enableIf: [
			SERVICES_TYPE.LENSES_ONLY,
			SERVICES_TYPE.FRAME_AND_LENSES,
			SERVICES_TYPE.MONOFOCAL_LENS,
			SERVICES_TYPE.BIFOCAL_LENS,
			SERVICES_TYPE.TRIFOCAL_LENS,
			SERVICES_TYPE.PROGRESSIVE_LENS,
			SERVICES_TYPE.FRAME_AND_MONOFOCAL_LENS,
			SERVICES_TYPE.FRAME_AND_BIFOCAL_LENS,
			SERVICES_TYPE.FRAME_AND_TRIFOCAL_LENS,
			SERVICES_TYPE.FRAME_AND_PROGRESSIVE_LENS,
		],
	},
	{
		label: 'newClaim.materials.contacts',
		value: SERVICES_TYPE.CONTACTS,
		enableIf: [SERVICES_TYPE.CONTACTS],
	},
	{
		label: 'newClaim.materials.noMat',
		value: -2,
		enableIf: [SERVICES_TYPE.EXAM, SERVICES_TYPE.EXAM_VDU],
	},
];

export const optionsSpecificLens: {
	label: string;
	labelCap?: string;
	value: number;
	statisticValue?: number;
	enableIf: number[];
}[] = [
	{
		label: 'newClaim.lensType.sv',
		value: SERVICES_TYPE.LENSES_ONLY,
		statisticValue: LENS_TYPES.MONOFOCAL_LENS,
		enableIf: [SERVICES_TYPE.LENSES_ONLY, SERVICES_TYPE.FRAME_AND_LENSES],
	},
	{
		label: 'newClaim.lensType.bi',
		value: SERVICES_TYPE.LENSES_ONLY,
		statisticValue: LENS_TYPES.BIFOCAL_LENS,
		enableIf: [SERVICES_TYPE.LENSES_ONLY, SERVICES_TYPE.FRAME_AND_LENSES],
	},
	{
		label: 'newClaim.lensType.tri',
		value: SERVICES_TYPE.LENSES_ONLY,
		statisticValue: LENS_TYPES.TRIFOCAL_LENS,
		enableIf: [SERVICES_TYPE.LENSES_ONLY, SERVICES_TYPE.FRAME_AND_LENSES],
	},
	{
		label: 'newClaim.lensType.pro',
		value: SERVICES_TYPE.LENSES_ONLY,
		statisticValue: LENS_TYPES.PROGRESSIVE_LENS,
		enableIf: [SERVICES_TYPE.LENSES_ONLY, SERVICES_TYPE.FRAME_AND_LENSES],
	},
	{
		label: 'newClaim.lensType.mono',
		value: SERVICES_TYPE.FRAME_AND_MONOFOCAL_LENS,
		statisticValue: LENS_TYPES.MONOFOCAL_LENS,
		enableIf: [SERVICES_TYPE.MONOFOCAL_LENS],
	},
	{
		label: 'newClaim.lensType.bi',
		value: SERVICES_TYPE.FRAME_AND_BIFOCAL_LENS,
		statisticValue: LENS_TYPES.BIFOCAL_LENS,
		enableIf: [SERVICES_TYPE.BIFOCAL_LENS],
	},
	{
		label: 'newClaim.lensType.tri',
		value: SERVICES_TYPE.FRAME_AND_TRIFOCAL_LENS,
		statisticValue: LENS_TYPES.TRIFOCAL_LENS,
		enableIf: [SERVICES_TYPE.TRIFOCAL_LENS],
	},
	{
		label: 'newClaim.lensType.pro',
		value: SERVICES_TYPE.FRAME_AND_PROGRESSIVE_LENS,
		statisticValue: LENS_TYPES.PROGRESSIVE_LENS,
		enableIf: [SERVICES_TYPE.PROGRESSIVE_LENS],
	},
	{
		label: 'newClaim.lensType.mono',
		value: SERVICES_TYPE.FRAME_AND_MONOFOCAL_LENS,
		statisticValue: LENS_TYPES.MONOFOCAL_LENS,
		enableIf: [SERVICES_TYPE.FRAME_AND_MONOFOCAL_LENS],
	},
	{
		label: 'newClaim.lensType.bi',
		value: SERVICES_TYPE.FRAME_AND_BIFOCAL_LENS,
		statisticValue: LENS_TYPES.BIFOCAL_LENS,
		enableIf: [SERVICES_TYPE.FRAME_AND_BIFOCAL_LENS],
	},
	{
		label: 'newClaim.lensType.tri',
		value: SERVICES_TYPE.FRAME_AND_TRIFOCAL_LENS,
		statisticValue: LENS_TYPES.TRIFOCAL_LENS,
		enableIf: [SERVICES_TYPE.FRAME_AND_TRIFOCAL_LENS],
	},
	{
		label: 'newClaim.lensType.pro',
		value: SERVICES_TYPE.FRAME_AND_PROGRESSIVE_LENS,
		statisticValue: LENS_TYPES.PROGRESSIVE_LENS,
		enableIf: [SERVICES_TYPE.FRAME_AND_PROGRESSIVE_LENS],
	},
];

export const allBenefits = [
	...optionsExam,
	...optionsMaterials,
	...optionsSpecificLens,
];
export interface IValidationError {
	field: number;
	message: string;
	value: string;
}

export type ReasonCode = {
	code: string;
	groupCode: string;
	groupLabel: string;
	label: string;
	typologyCode: string;
	typologyLabel: string;
};

export enum REASON_CODE_TYPOLOGY {
	VOID = 'void',
	CANCEL = 'cancel',
	PENDING = 'pending',
	REJECT = 'rejected',
	RETURN = 'return',
}
